import { Component, DestroyRef, input, inject, signal } from '@angular/core';
import { OverageDependentFormValue } from './overage-dependent.component';
import { ReportsAsync } from '../../store/reports/async';
import { IAdhocReportReq, IConsolidatedReq, INotices, IReportsState } from '../../store/reports/state';
import { Observable } from 'rxjs';
import { getSelectedAdminPlan } from '../../login/store/selectors';
import { ILoginState } from '../../login/store/state';
import { getODNoticeRes, isFetchingODReport, odErrors } from '../../store/reports/selector';
import { initialReportsState } from '../../store/reports/initialState';
import moment from 'moment';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

const OVERAGE_AS_OF_DATE_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY'];

@Component({
    selector: 'app-overage-dependent',
    template: `
      <app-overage-dependent-page
        [reportType]="reportType()"
        [selectedAdminPlan]="selectedAdminPlan()"
        [isFetching]="isFetchingODReport()"
        [errors]="errors()"
        [notices]="getODNoticeRes()"
        [adhocReportUrl]="adhocReportUrl()"
        [consolidatedReportUrl]="consolidatedReportUrl()"
        (processFormData)="processFormData($event)"
        (resetFormData)="resetFormData()"
        (getODNoticeDoc)="getODNoticeDocument($event)"></app-overage-dependent-page>
    `,
    standalone: false
})
export class OverageDependentContainer {
  readonly luxId = 'BMT-OverageDep-Reports' + this.constructor.name + '-OverDNC';
  private readonly destroyRef = inject(DestroyRef);
  readonly reportType = input<'Notice' | 'Adhoc Report' | 'Term Consolidated Report'>('Notice');
  readonly isFetchingODReport = toSignal(this.lux.get(isFetchingODReport), { initialValue: false });
  readonly errors = toSignal(this.lux.get(odErrors), { initialValue: '' });
  readonly selectedAdminPlan = toSignal(this.lux.get(getSelectedAdminPlan, this.luxId), { initialValue: '' });
  readonly getODNoticeRes = toSignal(this.lux.get(getODNoticeRes, this.luxId), { initialValue: [] as INotices[] });
  readonly adhocReportUrl = signal<string | undefined>(undefined);
  readonly consolidatedReportUrl = signal<string | undefined>(undefined);

  constructor(
    private readonly reportsAsync: ReportsAsync,
    private readonly lux: Lux<{ reportsState: IReportsState, login: ILoginState }>
  ) {
  }

  processFormData(value: OverageDependentFormValue): void {
    this.adhocReportUrl.set(undefined);
    this.consolidatedReportUrl.set(undefined);
    this.lux.set(state => state.reportsState, { adhocReportError: '', overageDepNoticeError: '', consolidatedReportErrors: '' });
    const planAcronym = value.planAcronym || this.selectedAdminPlan();
    if (this.reportType() === 'Notice') {
      this.lux.set(state => state.reportsState.overageDepNoticeReq.searchCriteria, { ...value, planAcronym });
      this.runRequest(this.reportsAsync.getODNotice());
    } else if (this.reportType() === 'Adhoc Report') {
      const req: IAdhocReportReq = {
        planAcronym,
        clientSpecifiedId: value.clientSpecifiedId,
        subclientSpecifiedId: value.subclientSpecifiedId,
        asOf: moment(value.asOf, OVERAGE_AS_OF_DATE_FORMATS, true).format('YYYY-MM-DD'),
        ageRange: {
          start: Number(value.startOfAgeRange),
          end: Number(value.uptoAgeRange)
        }
      };
      this.lux.set(state => state.reportsState.adhocReportReq, req);
      this.runRequest(this.reportsAsync.getAdhocReport(req), res => {
        this.adhocReportUrl.set(this.formExcelBlobUrl(res as BlobPart | null, 'adhocReportError'));
      });
    } else if (this.reportType() === 'Term Consolidated Report') {
      const req: IConsolidatedReq & { views: string[] } = {
        planAcronym,
        clientSpecifiedId: value.clientSpecifiedId,
        month: Number(value.month),
        year: value.year,
        views: ['fullSsn']
      };
      this.runRequest(this.reportsAsync.getConsolidatedReport(req), res => {
        this.consolidatedReportUrl.set(this.formExcelBlobUrl(res as BlobPart | null, 'consolidatedReportError'));
      });
    }
  }

  private runRequest(request$: Observable<unknown>, next?: (value: unknown) => void): void {
    request$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: value => next?.(value)
      });
  }

  formExcelBlobUrl(blobResponse: BlobPart | null, errorKey: 'adhocReportError' | 'consolidatedReportError'): string | undefined {
    if (blobResponse !== null) {
      const blob = new Blob([blobResponse], { type: 'application/vnd.ms-excel' });
      if (blob.size !== 0) {
        return URL.createObjectURL(blob);
      }
    }

    this.lux.set(state => state.reportsState, { [errorKey]: 'No data based on query' });
    return undefined;
  }

  resetFormData(): void {
    this.adhocReportUrl.set(undefined);
    this.consolidatedReportUrl.set(undefined);
    this.lux.set(state => state.reportsState.overageDepNoticeRes, initialReportsState.overageDepNoticeRes);
  }

  getODNoticeDocument(req: INotices): void {
    this.runRequest(this.reportsAsync.getPDFDocument(req));
  }
}
